<script setup lang="ts">
import BreadCrumb from "@/components/BreadCrumb.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { provide, ref, watch } from "vue";

import useHasPermissionUser from "@/composables/useHasPermissionUser";
import useInternalUser from "../composables/useInternalUser";
import AccountSettingsTab from "./partials/edit/AccountSettingsTab.vue";
import NotificationsTab from "./partials/edit/NotificationsTab.vue";
import PersonalInformationTab from "./partials/edit/PersonalInformationTab.vue";

const { user, isLoading, isFetching, refetch } = useInternalUser({});

const pages = ref<any[]>([]);

const { hasPermissionUser, abilities, redirectTo404 } = useHasPermissionUser();

enum ViewPermissionUser {
  UpdateUsers = "update-internal",
  ViewEmailsUser = "view-emails",
  ViewPhonesUser = "view-phones",
}

enum EntityTypes {
  User = "App\\Models\\User",
}

const tabs = ref([
  {
    name: "Información personal",
    hasPermission: [ViewPermissionUser.UpdateUsers],
    component: PersonalInformationTab,
    entityType: EntityTypes.User,
  },
  {
    name: "Ajustes de la cuenta",
    hasPermission: [ViewPermissionUser.UpdateUsers],
    component: AccountSettingsTab,
    entityType: EntityTypes.User,
  },
  // {
  //   name: "Notificaciones",
  //   hasPermission: [
  //     ViewPermissionUser.ViewEmailsUser,
  //     ViewPermissionUser.ViewPhonesUser,
  //   ],
  //   component: NotificationsTab,
  //   entityType: EntityTypes.User,
  // },
]);

const queryClient = useQueryClient();

const invalidateQueryData = async () => {
  queryClient.invalidateQueries(["user", Number(user.value?.id)]);
  queryClient.invalidateQueries(["user"]);
  await refetch();
};

provide("editUserProvides", { invalidateQueryData, isFetching });

watch(
  user,
  (value) => {
    if (value) {
      pages.value = [
        { name: "Usuarios", to: { name: "users.index" }, current: false },
        {
          name: `Editar ${value.name}`,
          to: { name: "users.edit.internal", params: { id: value.id } },
          current: true,
        },
      ];
    }
  },
  {
    immediate: true,
  },
);

watch(
  abilities,
  () => {
    redirectTo404("update-internal", "App\\Models\\User");
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <BreadCrumb :pages="pages" />

  <TabGroup
    v-if="user && !isLoading"
    as="div"
    class="flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto px-4 lg:px-[2.75rem] py-2 mb-7"
  >
    <TabList
      as="aside"
      class="flex flex-col gap-4 lg:w-1/3 lg:max-w-[13.75rem] !lg:sticky top-20"
    >
      <RouterLink
        class="flex justify-center"
        :to="{ name: 'users.show', params: { id: user.id } }"
      >
        <img
          class="rounded-full object-contain mb-2 max-h-[5rem]"
          :src="user.avatar"
        />
      </RouterLink>

      <nav
        v-dragscroll.x
        class="no-scrollbar flex touch-pan-x px-2 lg:p-0 w-full lg:max-w-[13.75rem] flex-row lg:flex-col gap-2 overflow-x-auto"
      >
        <Tab
          v-for="(tab, i) in tabs.filter((tab) =>
            tab.hasPermission.some((permission) =>
              hasPermissionUser(permission, tab.entityType),
            ),
          )"
          :key="i"
          v-slot="{ selected }"
          class="rounded-md w-[13.75rem]"
        >
          <button
            :class="[
              'tab fade-transition',
              selected ? 'bg-[#253C70]' : 'bg-[#1F2937]',
            ]"
          >
            {{ tab.name }}
          </button>
        </Tab>
      </nav>
    </TabList>

    <TabPanels as="div" class="w-full">
      <TabPanel
        v-for="(Element, index) in tabs.filter((tab) =>
          tab.hasPermission.some((permission) =>
            hasPermissionUser(permission, tab.entityType),
          ),
        )"
        :key="index"
      >
        <component :is="Element.component" :user="user" />
      </TabPanel>
    </TabPanels>
  </TabGroup>

  <LoadingModal :when="isLoading" />
</template>

<style scoped>
.tab {
  @apply w-full py-3 px-2 font-medium whitespace-nowrap rounded-md text-white focus:outline-none select-none;
}

.header {
  @apply font-semibold;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
